/**
 * Iter 234 · /airq/gov-login — DGCA + State Aviation dedicated sign-in.
 * Same auth endpoints as the operator login, but with a regulator-first
 * dark UI and helpful copy for government users.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, AirplaneTilt, ArrowLeft, LockKey } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "airq_account_token";

export default function AirqGovLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpStage, setOtpStage] = useState(null);
  const [otp, setOtp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/airq-auth/login`, { email, password });
      if (r.data.requires_otp) {
        setOtpStage(r.data);
        toast.success(`Code sent to ${r.data.email_masked}`);
      } else if (r.data.subtype !== "dgca" && r.data.subtype !== "state_aviation") {
        toast.error("This portal is for DGCA / State Aviation accounts");
      } else {
        localStorage.setItem(TOKEN_KEY, r.data.access_token);
        nav("/airq/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    }
    setLoading(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/airq-auth/login/verify-otp`,
        { challenge_id: otpStage.challenge_id, otp });
      if (r.data.subtype !== "dgca" && r.data.subtype !== "state_aviation") {
        toast.error("Non-regulator account — use the operator portal");
      } else {
        localStorage.setItem(TOKEN_KEY, r.data.access_token);
        toast.success(`Welcome, ${r.data.account.name}`);
        nav("/airq/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong OTP");
    }
    setLoading(false);
  };
  const resend = async () => {
    try {
      const r = await axios.post(`${API}/airq-auth/login/resend-otp`,
        { challenge_id: otpStage.challenge_id });
      setOtpStage(r.data); setOtp("");
      toast.success("New code sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Resend failed"); }
  };

  return (
    <div className="min-h-screen text-[#E2E8F0] flex items-center justify-center p-6"
           data-testid="airq-gov-login-root"
           style={{ background: "radial-gradient(1000px 600px at 20% 0%, rgba(59,130,246,.15), transparent), radial-gradient(700px 500px at 100% 100%, rgba(20,184,166,.12), transparent), #020617" }}>
      <div className="w-full max-w-md">
        <Link to="/airq" data-testid="gov-back"
                className="inline-flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#67E8F9] mb-6">
          <ArrowLeft size={12}/> Back to AirQ
        </Link>
        <form onSubmit={otpStage ? verify : submit}
                data-testid="airq-gov-form"
                className="rounded-2xl border border-white/10 bg-white/[.03] backdrop-blur p-7">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-lg bg-[#67E8F9]/15 flex items-center justify-center">
              <ShieldCheck size={22} className="text-[#67E8F9]"/>
            </div>
            <div>
              <div className="font-heading text-xl">Government sign-in</div>
              <div className="text-xs text-[#94A3B8]">
                DGCA · State Aviation authorities
              </div>
            </div>
          </div>
          {!otpStage ? (
            <>
              <label className="block text-[10px] uppercase tracking-[.2em] text-[#67E8F9] mb-1">
                Official email
              </label>
              <input type="email" required value={email}
                       onChange={e => setEmail(e.target.value)}
                       data-testid="gov-email"
                       className="w-full bg-transparent border border-white/15 rounded px-3 py-2.5 mb-4 text-sm text-white placeholder:text-[#64748B] focus:border-[#67E8F9] outline-none"
                       placeholder="officer@dgca.gov.in"/>
              <label className="block text-[10px] uppercase tracking-[.2em] text-[#67E8F9] mb-1">
                Password
              </label>
              <input type="password" required value={password}
                       onChange={e => setPassword(e.target.value)}
                       data-testid="gov-password"
                       className="w-full bg-transparent border border-white/15 rounded px-3 py-2.5 mb-5 text-sm text-white focus:border-[#67E8F9] outline-none"/>
              <button type="submit" disabled={loading}
                        data-testid="gov-submit"
                        className="w-full py-3 rounded-lg bg-[#67E8F9] text-[#0F172A] font-semibold hover:bg-white transition disabled:opacity-50">
                {loading ? "Signing in…" : "Continue"}
              </button>
              <p className="mt-5 text-[11px] text-[#64748B] flex items-center gap-1">
                <LockKey size={11}/> Every sign-in is verified with an email OTP.
              </p>
            </>
          ) : (
            <>
              <div className="mb-4 p-3 rounded-lg bg-[#67E8F9]/10 border border-[#67E8F9]/20 text-xs">
                A 6-digit code was sent to <b>{otpStage.email_masked}</b>. It expires in 10 minutes.
              </div>
              <label className="block text-[10px] uppercase tracking-[.2em] text-[#67E8F9] mb-1">
                One-time code
              </label>
              <input required value={otp} inputMode="numeric" maxLength={6}
                       onChange={e => setOtp(e.target.value.replace(/\D/g,"").slice(0,6))}
                       data-testid="gov-otp"
                       className="w-full bg-transparent border border-white/15 rounded px-3 py-3 mb-5 text-lg text-center tracking-[.7em] font-mono text-white focus:border-[#67E8F9] outline-none"/>
              <button type="submit" disabled={loading || otp.length !== 6}
                        data-testid="gov-verify"
                        className="w-full py-3 rounded-lg bg-[#67E8F9] text-[#0F172A] font-semibold hover:bg-white transition disabled:opacity-50">
                {loading ? "Verifying…" : "Verify & enter portal"}
              </button>
              <div className="mt-4 flex justify-between text-xs">
                <button type="button" onClick={resend} data-testid="gov-resend"
                          className="text-[#67E8F9]">Resend code</button>
                <button type="button" onClick={() => { setOtpStage(null); setOtp(""); }}
                          className="text-[#94A3B8]">Change email</button>
              </div>
            </>
          )}
        </form>
        <div className="mt-6 text-[11px] text-[#64748B] text-center">
          Not a regulator? <Link to="/airq/login" className="text-[#94A3B8] hover:text-[#67E8F9]">
            Operator sign-in <AirplaneTilt size={11} className="inline"/>
          </Link>
        </div>
      </div>
    </div>
  );
}
